import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowLeft,
  Store,
  Users,
  BarChart3,
  Link,
  CheckCircle,
  Star,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

const BecomeSeller = () => {
  const benefits = [
    {
      icon: <Store className="h-6 w-6" />,
      title: "Manage All Orders",
      description:
        "Centralized dashboard to track and manage all customer orders in one place",
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "See Customer Designs",
      description:
        "View the exact designs your customers want before production begins",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Track Performance",
      description:
        "Monitor your sales, revenue, and customer satisfaction metrics",
    },
    {
      icon: <Link className="h-6 w-6" />,
      title: "Unique Links",
      description:
        "Generate custom links to share with your customers for easy ordering",
    },
  ];

  const features = [
    "Real-time order notifications",
    "Customer design preview",
    "Automated order processing",
    "Revenue tracking and analytics",
    "Custom branding options",
    "Bulk order management",
    "Customer communication tools",
    "Quality control checkpoints",
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <RouterLink to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </RouterLink>
            <h1 className="text-xl font-bold">Become a Weaver</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
              <Store className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">Join Our Weaver Community</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            As a seller, this platform will help you manage all your orders and
            see the actual designs your customers want. Streamline your business
            and grow your Kente sales.
          </p>
          <RouterLink to="/login">
            <Button size="lg" className="px-8">
              Get Started
            </Button>
          </RouterLink>
        </div>

        {/* Benefits Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {benefits.map((benefit, index) => (
            <Card
              key={index}
              className="text-center hover:shadow-lg transition-shadow"
            >
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 text-primary">
                  {benefit.icon}
                </div>
                <h3 className="font-semibold mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {benefit.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Section */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              Platform Features
            </CardTitle>
            <CardDescription className="text-center">
              Everything you need to run a successful Kente business
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl text-center">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="font-semibold mb-2">Sign Up</h3>
                <p className="text-sm text-muted-foreground">
                  Create your seller account and set up your business profile
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="font-semibold mb-2">Share Your Link</h3>
                <p className="text-sm text-muted-foreground">
                  Generate your unique designer link and share it with customers
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="font-semibold mb-2">Manage Orders</h3>
                <p className="text-sm text-muted-foreground">
                  Receive orders, view customer designs, and track your business
                  growth
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Testimonials */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              What Our Weavers Say
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-lg p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "This platform has transformed my Kente business. I can now
                  manage all my orders efficiently and see exactly what my
                  customers want before I start production."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Kwame Asante</p>
                    <p className="text-sm text-muted-foreground">
                      Kente Artisan
                    </p>
                  </div>
                </div>
              </div>
              <div className="border rounded-lg p-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "The dashboard makes it so easy to track my sales and manage
                  customer orders. The design preview feature has eliminated all
                  confusion about what customers want."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Ama Osei</p>
                    <p className="text-sm text-muted-foreground">
                      Textile Business Owner
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Selling?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join hundreds of sellers who are already using our platform to
              grow their Kente businesses and serve customers worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <RouterLink to="/login">
                <Button size="lg" className="px-8">
                  Get Started Now
                </Button>
              </RouterLink>
              <RouterLink to="/contact">
                <Button variant="outline" size="lg" className="px-8">
                  Learn More
                </Button>
              </RouterLink>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BecomeSeller;
